import * as msal from "@azure/msal-browser";
import { IAuthContextConfig, IAuthenticationContext, IUser } from "~/ad-authentication/Interfaces";
import { authConfig } from "~/ad-authentication";

export class MsalAuthenticationContext implements IAuthenticationContext {
    private authContext: msal.PublicClientApplication;
    private initialized: Promise<void>;

    public constructor(aadConfig: IAuthContextConfig) {
        this.authContext = new msal.PublicClientApplication({ auth: aadConfig });
        this.initialized = this.initializeMsal();
    }

    private async initializeMsal(): Promise<void> {
        await this.authContext.initialize();
        // Default to using the first account if no account is active on page load
        if (!this.authContext.getActiveAccount() && this.authContext.getAllAccounts().length > 0) {
            this.authContext.setActiveAccount(this.authContext.getAllAccounts()[0]);
        }
    }

    private async login() {
        await this.initialized;
        await this.authContext.loginRedirect(authConfig.getLoginRequest());
        this.authContext.setActiveAccount(this.authContext.getAllAccounts()[0]);
    }

    public async acquireToken(resourceId: string): Promise<string> {
        await this.initialized;

        await this.authContext.handleRedirectPromise();
        
        const account = this.getAccountInfo();
        if (!account) {
            await this.login();
        }

        const accessTokenRequest = {
            scopes: [resourceId + "/.default"],
            account: account || this.authContext.getActiveAccount(),
            authority: authConfig.getAuthority(),
        };

        try {
            const accessTokenResponse = await this.authContext.acquireTokenSilent(accessTokenRequest);
            // Acquire token silent success
            return accessTokenResponse.accessToken;
        } catch (error) {
            //Acquire token silent failure, and send an interactive request
            if (error instanceof msal.InteractionRequiredAuthError) {
                await this.authContext.acquireTokenRedirect(accessTokenRequest);
            }
            return Promise.reject(error);
        }
    }

    public async getCurrentUser(): Promise<IUser> {

        const activeAccount = this.getAccountInfo();

        const currentUser: IUser = {
            userName: activeAccount.username,
            profile: { oid: activeAccount.homeAccountId },
        };
        return Promise.resolve(currentUser);
    }

    public async tryHandleWindowCallBack(): Promise<boolean> {
        await this.initialized;
        const response = await this.authContext
            .handleRedirectPromise()
            .then(async () => {
                return Promise.resolve(true);
            })
            .catch(async () => {
                return Promise.resolve(false);
            });

        return response;
    }

    public async isTokenRenewalInProgress(): Promise<boolean> {
        if (window.parent !== window) {
            return Promise.resolve(true);
        }
        return Promise.resolve(false);
    }

    private getAccountInfo(): msal.AccountInfo {
        return (
            this.authContext.getActiveAccount() ?? //This will only return a non-null value if setActiveAccount API is already set
            this.authContext.getAllAccounts()[0] // If active account is not set, we will use first account as default account.
        ); 
    }
}